import { useMemo } from 'react';
import { css } from '@emotion/react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useAppScopeStore } from '@frontend/scope';
import { SpinningLoader } from '@frontend/design-system';
import { useSchedulingLocationInfo } from '../../../hooks/use-scheduling-location-info';
import { DEFAULT_SCHEDULING_MODE } from './constants';
import { LocationSchedulingModeListRow } from './location-scheduling-mode-list-row';
import { SchedulingModeEditor } from './scheduling-mode-editor';
import { SchedulingMode } from './types';

export const LocationSchedulingModeList = () => {
  const { selectedLocationIds } = useAppScopeStore();

  const { getOnlineSchedulingReadyLocationIds } = useSchedulingLocationInfo();
  const onlineSchedulingEnabledLocations = getOnlineSchedulingReadyLocationIds();

  const onlineSchedulingEnabledLocationIdsSet = useMemo(
    () => new Set(onlineSchedulingEnabledLocations),
    [onlineSchedulingEnabledLocations]
  );

  const sortedLocationIds = useMemo(() => {
    const enabled: string[] = [];
    const disabled: string[] = [];
    selectedLocationIds.forEach((locationId) => {
      if (onlineSchedulingEnabledLocationIdsSet.has(locationId)) {
        enabled.push(locationId);
      } else {
        disabled.push(locationId);
      }
    });
    return [...enabled, ...disabled];
  }, [selectedLocationIds, onlineSchedulingEnabledLocationIdsSet]);

  const listLocationSettingsQuery = SchedulerV3.Queries.useListLocationSettingsQuery({
    request: { locationIds: selectedLocationIds },
    options: { enabled: selectedLocationIds.length > 0 },
  });

  const getLocationSchedulingModeValue = (locationId: string): SchedulingMode => {
    return (listLocationSettingsQuery.data?.locationsSettings?.[locationId]?.osChannelMode ??
      DEFAULT_SCHEDULING_MODE) as SchedulingMode;
  };

  if (listLocationSettingsQuery.isLoading) {
    return <SpinningLoader size='medium' />;
  }

  // Single location
  if (selectedLocationIds.length === 1) {
    return (
      <SchedulingModeEditor
        locationId={selectedLocationIds[0]}
        value={getLocationSchedulingModeValue(selectedLocationIds[0])}
      />
    );
  }

  // Multi location
  return (
    <div css={listContainerStyles}>
      {sortedLocationIds.map((locationId) => {
        const value = getLocationSchedulingModeValue(locationId);
        const hasOnlineScheduling = onlineSchedulingEnabledLocationIdsSet.has(locationId);
        return (
          <LocationSchedulingModeListRow
            key={locationId}
            locationId={locationId}
            value={value}
            hasOnlineScheduling={hasOnlineScheduling}
          />
        );
      })}
    </div>
  );
};

const listContainerStyles = css`
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-md);
  margin-top: var(--fabric-space-margin-md);
`;
